import { createApi } from '@reduxjs/toolkit/query/react';

import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';

import type {
  MetaCampaignsResponse,
  MetaWeeklyInsightsResponse,
} from '../routes/meta-dashboard/types';

/**
 * Data-analytics backend proxies. These replace the three calls that used to
 * hit third-party APIs directly from the browser with a credential in the
 * client (Rybbit's hardcoded bearer, Meta's `access_token` in the query
 * string). Every secret now lives server-side; the frontend only ever calls
 * our own `/v1/data-analytics/...` routes, authenticated by the normal app
 * token via `customBaseQuery`.
 *
 * The backend wraps every response in the standard `ApiResponse<T>` envelope
 * (global ResponseFormatterInterceptor), so the raw upstream body arrives as
 * `envelope.data`. Each endpoint unwraps that one extra layer in
 * `transformResponse`, returning the upstream shape callers already expect.
 */

/** Query params forwarded to the Meta insights proxy (pass-through contract). */
export interface MetaInsightsParams {
  /** Single-campaign target. Omitted for account-level (group / all-active). */
  campaignId?: string;
  fields: string;
  /** JSON string: {"since":…,"until":…}. */
  time_range: string;
  /** '1' | '7' | 'monthly'. */
  time_increment: string;
  limit: string;
  /** 'account' for group / all-active aggregation. */
  level?: string;
  /** JSON string of the Meta `filtering` spec. */
  filtering?: string;
}

/** Query params forwarded to the Rybbit users proxy. */
export interface RybbitUsersParams {
  siteId: number;
  startDate: string;
  endDate: string;
  timeZone: string;
  page: number;
  limit: number;
}

const BASE = '/v1/data-analytics';

export const dataAnalyticsApi = createApi({
  reducerPath: 'dataAnalyticsApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    // Meta Graph campaign list — replaces the client-side `access_token` fetch.
    getMetaCampaigns: builder.query<MetaCampaignsResponse, void>({
      query: () => `${BASE}/meta/campaigns`,
      transformResponse: (r: ApiResponse<MetaCampaignsResponse>) => r.data,
    }),

    // Meta Graph insights — pass-through of the same params the client used to
    // build, minus the token/account id (resolved server-side).
    getMetaInsights: builder.query<
      MetaWeeklyInsightsResponse,
      MetaInsightsParams
    >({
      query: (params) => {
        const search = new URLSearchParams();
        // Only append the params that are actually set (campaignId / level /
        // filtering are conditional), so account-level requests omit campaignId
        // and the backend falls back to the ad account id.
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') search.set(key, value);
        });
        return `${BASE}/meta/insights?${search.toString()}`;
      },
      transformResponse: (r: ApiResponse<MetaWeeklyInsightsResponse>) => r.data,
    }),

    // Rybbit website-analytics users — replaces the hardcoded-bearer fetch.
    // Normalises the upstream body (array or `{ data: [] }`) to a flat array so
    // the caller's pagination loop stays simple.
    getRybbitUsers: builder.query<unknown[], RybbitUsersParams>({
      query: ({ siteId, startDate, endDate, timeZone, page, limit }) => {
        const search = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          time_zone: timeZone,
          page: page.toString(),
          limit: limit.toString(),
        });
        return `${BASE}/rybbit/sites/${siteId}/users?${search.toString()}`;
      },
      transformResponse: (r: ApiResponse<unknown[] | { data?: unknown[] }>) => {
        const body = r.data;
        return Array.isArray(body) ? body : (body?.data ?? []);
      },
    }),
  }),
});

export const {
  useGetMetaCampaignsQuery,
  useGetMetaInsightsQuery,
  useLazyGetRybbitUsersQuery,
} = dataAnalyticsApi;
